/**
 * Purchase Invoice mapping — Access `frmReportCashFlowQryMuaHang_add`.
 *
 * Pick an ERP purchase invoice + a MISA invoice; Save writes the link into
 * `report_cashflow_map_sohoadon_muahang` (New if no map id, else Update).
 * All preview fields are read-only; the two combos drive the form.
 */
import { Autocomplete, Box, Dialog, DialogContent, TextField } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowDetailLookupQuery,
  useSaveCashflowMapMuaHangMutation,
  type MapMuaHang,
} from '../../../../store/api/reportCashflowApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { FORM_GRID_2COL } from '../../../../theme/responsive'
import { readonlyFieldSx } from './formHelpers'

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  row: MapMuaHang | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function MuaHangMapDialog({ open, mode, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [erpInput, setErpInput] = useState('')
  const [misaInput, setMisaInput] = useState('')
  const [erp, setErp] = useState<Row | null>(null)
  const [misa, setMisa] = useState<Row | null>(null)
  const [save, { isLoading }] = useSaveCashflowMapMuaHangMutation()

  const { data: erpData } = useGetCashflowDetailLookupQuery({ kind: 'erp_muahang', q: erpInput || undefined }, { skip: !open })
  const { data: misaData } = useGetCashflowDetailLookupQuery({ kind: 'misa_muahang', q: misaInput || undefined }, { skip: !open })

  const erpLabel = (r: Row) => `${s(r.erp_cp)} - ${s(r.erp_misa_code)} - ${s(r.erp_inv_no)}`
  const misaLabel = (r: Row) => `${s(r.nhacungcap)} - ${s(r.sohoadon)} - ${s(r.sochungtu)}`

  const erpOptions = useMemo(() => erpData?.items ?? [], [erpData])
  const misaOptions = useMemo(() => misaData?.items ?? [], [misaData])
  // Inject the seeded (edit) selection so the chip shows + no MUI "no match" warning.
  const erpMerged = useMemo(
    () => (erp && !erpOptions.some((o) => erpLabel(o) === erpLabel(erp)) ? [erp, ...erpOptions] : erpOptions),
    [erp, erpOptions],
  )
  const misaMerged = useMemo(
    () => (misa && !misaOptions.some((o) => s(o.refid) === s(misa.refid)) ? [misa, ...misaOptions] : misaOptions),
    [misa, misaOptions],
  )

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      setErp({ erp_cp: row.erpCp, erp_misa_code: row.erpMisaCode, erp_inv_no: row.erpInvNo, contract_no: row.contractNo, invoice_date: row.invoiceDate })
      setMisa({ refid: row.refid, nhacungcap: row.nhacungcap, sohoadon: row.sohoadon, sochungtu: row.sochungtu, ngayhoadon: row.ngayhoadon, tab_name: row.tabName })
    } else {
      setErp(null)
      setMisa(null)
    }
    setErpInput('')
    setMisaInput('')
  }, [open, mode, row])

  const handleSave = async () => {
    if (!canEdit) return
    if (!s(erp?.erp_inv_no) || !s(erp?.erp_cp)) {
      window.alert('Please choose ERP INVOICE without NULL in InvoiceNo, CP Code!')
      return
    }
    if (!row?.misaMapId && !s(misa?.refid)) {
      window.alert('Please choose MISA INVOICE in the INSERT query!')
      return
    }
    try {
      const res = await save({
        misaMapId: mode === 'edit' ? row?.misaMapId ?? null : null,
        erpCp: s(erp?.erp_cp),
        erpInvNo: s(erp?.erp_inv_no),
        erpInvDate: erp?.invoice_date ? s(erp.invoice_date) : null,
        erpMisaCode: s(erp?.erp_misa_code),
        misaCp: s(misa?.nhacungcap),
        misaInvNo: s(misa?.sohoadon),
        misaInvDate: misa?.ngayhoadon ? s(misa.ngayhoadon) : null,
        misaRef: s(misa?.refid),
        misaObjectCode: s(misa?.tab_name),
      }).unwrap()
      snack.success(res.message || 'Successfully Added')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the mapping.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleBar
        title="Purchase Invoice - Misa Invoice"
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit}
      />
      <DialogContent>
        <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
          <Autocomplete
            size="small"
            sx={{ gridColumn: '1 / -1' }}
            options={erpMerged}
            getOptionLabel={erpLabel}
            isOptionEqualToValue={(o, v) => erpLabel(o) === erpLabel(v)}
            value={erp}
            onChange={(_, v) => setErp(v)}
            inputValue={erpInput}
            onInputChange={(_, v) => setErpInput(v)}
            renderInput={(p) => <TextField {...p} label="ERP Invoice" InputLabelProps={{ shrink: true }} />}
          />
          <TextField label="ERP - Contract No" value={s(erp?.contract_no)} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <TextField label="ERP - Misa Code" value={s(erp?.erp_misa_code)} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <Autocomplete
            size="small"
            sx={{ gridColumn: '1 / -1' }}
            options={misaMerged}
            getOptionLabel={misaLabel}
            isOptionEqualToValue={(o, v) => s(o.refid) === s(v.refid)}
            value={misa}
            onChange={(_, v) => setMisa(v)}
            inputValue={misaInput}
            onInputChange={(_, v) => setMisaInput(v)}
            renderInput={(p) => <TextField {...p} label="MISA Invoice" InputLabelProps={{ shrink: true }} />}
          />
          <TextField label="Misa - Tab Name" value={s(misa?.tab_name)} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <TextField label="Misa - Refid" value={s(misa?.refid)} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
